"use client";

import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import {
  closestCenter,
  DragOverlay,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CircleHelp, Loader2, Plus } from "lucide-react";
import { useEcomFeedback } from "../../ui";
import { ecomImageApi, type WorkflowUIConfig } from "../../lib/ecom-image-api";
import {
  DETAIL_REPLICA_BLANK_FLAGS_KEY,
  DETAIL_REPLICA_CROP_SIZES_KEY,
  DETAIL_REPLICA_RATIOS_KEY,
  DETAIL_REPLICA_REFERENCE_IMAGES_KEY,
  moveDetailReplicaIndexOrder,
  removeDetailReplicaScreen,
  restoreDetailReplicaScreen,
  DETAIL_REPLICA_MAX_SCREENS,
  type DetailReplicaDeletedScreen,
  type DetailReplicaState,
  resolveDetailReplicaComponentKeys,
  resolveDetailReplicaGate,
  resolveDetailReplicaRevealBehavior,
  resolveDetailReplicaScrollGuardMs,
  resolveDetailReplicaState,
  resolveDetailReplicaThumbnailSize,
  revealReferenceScrollTop,
} from "../../lib/ecom-detail-page-replica";
import DetailPageReplicaReorderDialog from "./DetailPageReplicaReorderDialog";
import EcomOnboardingGlowLights from "./EcomOnboardingGlowLights";
import SafeImage from "./SafeImage";
import { EcomBackIcon, EcomButton as Button, EcomModal, EcomTutorialPopover, EcomTutorialVideoModal } from "../../ui";
import { SELECTED_REFERENCE_BORDER } from "../../lib/ecom-detail-page-replica-styles";
import { resolveDetailReplicaUploadURL } from "../../lib/ecom-detail-replica-upload-url";
import {
  createFrameBatcher,
  ECOM_UPLOAD_CONCURRENCY,
  mapSettledWithConcurrency,
} from "../../lib/ecom-upload-batch";
import { resolveDetailReplicaTutorialConfig } from "../../lib/ecom-detail-replica-tutorial";
import { getHelpTipHoverPopoverPosition, type HelpTipPopoverPosition } from "../../lib/ecom-help-tip-popover-position";
import {
  getEcomHelpTipDeviceId,
  getEcomHelpTipDismissalStorageKey,
  isEcomHelpTipDismissed,
  setEcomHelpTipDismissed,
} from "../../lib/ecom-help-tip-dismissal";
import { IMAGE_ACCEPT, MAX_REVERSE_PROMPT_BYTES, isImageFile } from "../../lib/file-utils";
import { useShellState } from "@flowx/module-runtime/client";

/** 首帧本地预览的占位比例，真实比例由已渲染图片回填。 */
const FALLBACK_REFERENCE_ASPECT = 1;

function isKeyboardEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
    || target.isContentEditable
    || Boolean(target.closest('[contenteditable="true"]'));
}

type DetailPageReplicaWorkspaceProps = {
  uiConfig: WorkflowUIConfig | null;
  params: Record<string, unknown>;
  onParamChange: (key: string, value: unknown) => void;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  focusRequest?: { index: number; requestID: number } | null;
  onReferenceCountChange?: (count: number) => void;
  onUploadingChange?: (uploading: boolean) => void;
  uploadHoverPreview?: { url: string; alt?: string } | null;
};

type SortableDetailReplicaThumbnailProps = {
  url: string;
  sortableId: string;
  index: number;
  width: number;
  height: number;
  selected: boolean;
  onSelect: (index: number) => void;
};

function SortableDetailReplicaThumbnail({
  url,
  sortableId,
  index,
  width,
  height,
  selected,
  onSelect,
}: SortableDetailReplicaThumbnailProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });
  const style: CSSProperties = {
    width,
    height,
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: "none",
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <button
      ref={setNodeRef}
      type="button"
      data-detail-replica-thumbnail={index + 1}
      data-detail-replica-thumbnail-dragging={isDragging ? "true" : undefined}
      onClick={() => onSelect(index)}
      aria-current={selected ? "true" : undefined}
      aria-label={`定位到第 ${index + 1} 屏参考图`}
      className={`group relative block shrink-0 overflow-hidden border-0 bg-transparent p-0 ${
        isDragging
          ? "cursor-grabbing opacity-20"
          : "cursor-pointer"
      }`}
      style={style}
      {...attributes}
      {...listeners}
    >
      <img
        src={url}
        alt=""
        draggable={false}
        className="pointer-events-none block h-full w-full object-cover object-top"
      />
      {!selected && (
        <span
          data-detail-replica-thumbnail-dim
          className="pointer-events-none absolute inset-0 bg-black/55 transition-colors group-hover:bg-black/20"
        />
      )}
    </button>
  );
}

export default function DetailPageReplicaWorkspace({
  uiConfig,
  params,
  onParamChange,
  activeIndex,
  onActiveIndexChange,
  focusRequest,
  onReferenceCountChange,
  onUploadingChange,
  uploadHoverPreview,
}: DetailPageReplicaWorkspaceProps) {
  const feedback = useEcomFeedback();
  const keys = useMemo(() => resolveDetailReplicaComponentKeys(uiConfig), [uiConfig]);
  const state = useMemo(() => resolveDetailReplicaState(uiConfig, params), [params, uiConfig]);
  const gate = useMemo(() => resolveDetailReplicaGate(state), [state]);
  const tutorialConfig = useMemo(
    () => resolveDetailReplicaTutorialConfig(uiConfig),
    [uiConfig],
  );
  const { user } = useShellState();
  const [tutorialDeviceId, setTutorialDeviceId] = useState("server");
  const [tutorialDismissed, setTutorialDismissed] = useState(false);
  useEffect(() => {
    setTutorialDeviceId(getEcomHelpTipDeviceId());
  }, []);
  const tutorialDismissalKey = useMemo(() => {
    if (!tutorialConfig) return undefined;
    return getEcomHelpTipDismissalStorageKey({
      scopeKey: `u${user?.id || 0}:t${user?.current_team_id || 0}:d${tutorialDeviceId}`,
      workflowKey: uiConfig?.workflow_key || "detail-replica",
      componentParamKey: "detail_replica_tutorial",
      helpTip: {
        version: tutorialConfig.version,
        title: tutorialConfig.title,
        content: tutorialConfig.content,
        image_url: tutorialConfig.image_url,
        detail_url: tutorialConfig.detail_url,
        detail_media_type: tutorialConfig.detail_media_type,
      },
    });
  }, [tutorialConfig, tutorialDeviceId, uiConfig?.workflow_key, user?.current_team_id, user?.id]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  // 已完成完整解码的参考图 URL 集合；未就绪的图在列表里盖蒙版转圈，避免大图逐行加载露出。
  const [loadedReferenceUrls, setLoadedReferenceUrls] = useState<ReadonlySet<string>>(new Set());
  const loadedReferenceUrlsBatcherRef = useRef<ReturnType<typeof createFrameBatcher<string>> | null>(null);
  const aspectEpochBatcherRef = useRef<ReturnType<typeof createFrameBatcher<void>> | null>(null);
  if (!loadedReferenceUrlsBatcherRef.current) {
    loadedReferenceUrlsBatcherRef.current = createFrameBatcher((urls) => {
      setLoadedReferenceUrls((current) => {
        const next = new Set(current);
        urls.forEach((url) => next.add(url));
        return next.size === current.size ? current : next;
      });
    });
  }
  if (!aspectEpochBatcherRef.current) {
    aspectEpochBatcherRef.current = createFrameBatcher(() => setAspectEpoch((value) => value + 1));
  }
  const markReferenceLoaded = useCallback((url: string) => {
    loadedReferenceUrlsBatcherRef.current?.enqueue(url);
  }, []);
  const isReferencePending = useCallback((url: string) => (
    !loadedReferenceUrls.has(url) || url.startsWith("blob:")
  ), [loadedReferenceUrls]);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialPreviewOpen, setTutorialPreviewOpen] = useState(false);
  const [tutorialPreviewPinned, setTutorialPreviewPinned] = useState(false);
  const [tutorialPreviewPosition, setTutorialPreviewPosition] = useState<HelpTipPopoverPosition | null>(null);
  const tutorialButtonRef = useRef<HTMLButtonElement>(null);
  const tutorialPreviewRef = useRef<HTMLDivElement>(null);
  const tutorialCloseTimerRef = useRef<number | null>(null);
  const tutorialOpenRef = useRef(tutorialOpen);
  tutorialOpenRef.current = tutorialOpen;
  const [reorderOpen, setReorderOpen] = useState(false);
  const [draggedThumbnailIndex, setDraggedThumbnailIndex] = useState<number | null>(null);
  const [insertSlotIndex, setInsertSlotIndex] = useState<number | null>(null);
  const [insertDragOverIndex, setInsertDragOverIndex] = useState<number | null>(null);
  // 宽高比按 URL 同步写入 ref，保证插入后首帧就能锁住容器高度，避免先塌后撑。
  const aspectByUrlRef = useRef<Map<string, number>>(new Map());
  const [, setAspectEpoch] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const insertPlaceholderRef = useRef<HTMLLabelElement>(null);
  const thumbnailRailRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const referencesRef = useRef(state.references);
  const [revealTarget, setRevealTarget] = useState<{ index: number; token: number } | null>(null);
  const [thumbnailSize, setThumbnailSize] = useState(() => resolveDetailReplicaThumbnailSize(
    Number.POSITIVE_INFINITY,
    0,
  ));
  const revealTokenRef = useRef(0);
  const programmaticScrollUntilRef = useRef(0);
  const deletionHistoryRef = useRef<DetailReplicaDeletedScreen[]>([]);
  const thumbnailSensors = useSensors(
    useSensor(PointerSensor, {
      // dnd-kit 使用严格大于比较；0 代表首笔非零移动，静止按下仍保持点击。
      activationConstraint: { distance: 0 },
    }),
  );
  referencesRef.current = state.references;

  useEffect(() => () => {
    loadedReferenceUrlsBatcherRef.current?.dispose();
    aspectEpochBatcherRef.current?.dispose();
  }, []);

  useEffect(() => {
    setTutorialDismissed(isEcomHelpTipDismissed(tutorialDismissalKey));
    setTutorialPreviewOpen(false);
    setTutorialPreviewPinned(false);
  }, [tutorialDismissalKey]);

  const clearTutorialCloseTimer = useCallback(() => {
    if (tutorialCloseTimerRef.current !== null) {
      window.clearTimeout(tutorialCloseTimerRef.current);
      tutorialCloseTimerRef.current = null;
    }
  }, []);
  const updateTutorialPreviewPosition = useCallback(() => {
    const button = tutorialButtonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    setTutorialPreviewPosition(getHelpTipHoverPopoverPosition({
      anchor: rect,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    }));
  }, []);
  const openTutorialPreview = useCallback((pin = false) => {
    if (tutorialDismissed) return;
    clearTutorialCloseTimer();
    updateTutorialPreviewPosition();
    setTutorialPreviewOpen(true);
    setTutorialPreviewPinned((current) => current || pin);
    requestAnimationFrame(updateTutorialPreviewPosition);
  }, [clearTutorialCloseTimer, tutorialDismissed, updateTutorialPreviewPosition]);
  const closeTutorialPreview = useCallback(() => {
    clearTutorialCloseTimer();
    setTutorialPreviewOpen(false);
    setTutorialPreviewPinned(false);
  }, [clearTutorialCloseTimer]);
  const dismissTutorial = useCallback(() => {
    setEcomHelpTipDismissed(tutorialDismissalKey);
    setTutorialDismissed(true);
    setTutorialPreviewOpen(false);
    setTutorialPreviewPinned(false);
  }, [tutorialDismissalKey]);
  const scheduleTutorialPreviewClose = useCallback(() => {
    if (tutorialPreviewPinned) return;
    clearTutorialCloseTimer();
    tutorialCloseTimerRef.current = window.setTimeout(() => setTutorialPreviewOpen(false), 140);
  }, [clearTutorialCloseTimer, tutorialPreviewPinned]);
  useEffect(() => () => clearTutorialCloseTimer(), [clearTutorialCloseTimer]);
  useEffect(() => {
    if (!tutorialPreviewOpen) return;
    const reposition = () => updateTutorialPreviewPosition();
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (tutorialButtonRef.current?.contains(target) || tutorialPreviewRef.current?.contains(target)) return;
      closeTutorialPreview();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      const shouldRestoreFocus = tutorialPreviewRef.current?.contains(document.activeElement);
      closeTutorialPreview();
      if (shouldRestoreFocus) tutorialButtonRef.current?.focus();
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [closeTutorialPreview, tutorialPreviewOpen, updateTutorialPreviewPosition]);

  useLayoutEffect(() => {
    const rail = thumbnailRailRef.current;
    if (!rail) return;

    const updateThumbnailSize = () => {
      setThumbnailSize(resolveDetailReplicaThumbnailSize(
        rail.clientHeight,
        referencesRef.current.length,
      ));
    };

    updateThumbnailSize();
    const observer = new ResizeObserver(updateThumbnailSize);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [state.references.length]);

  const rememberAspectForUrl = useCallback((url: string, aspect: number, rerender = false) => {
    if (!url || !Number.isFinite(aspect) || aspect <= 0) return;
    const previous = aspectByUrlRef.current.get(url);
    aspectByUrlRef.current.set(url, aspect);
    if (rerender && (previous == null || Math.abs(previous - aspect) >= 0.001)) {
      aspectEpochBatcherRef.current?.enqueue(undefined);
    }
  }, []);

  const forgetAspectForUrl = useCallback((url: string) => {
    aspectByUrlRef.current.delete(url);
  }, []);

  const transferAspectUrl = useCallback((fromUrl: string, toUrl: string) => {
    const aspect = aspectByUrlRef.current.get(fromUrl);
    if (aspect != null) aspectByUrlRef.current.set(toUrl, aspect);
    aspectByUrlRef.current.delete(fromUrl);
  }, []);

  const updateReferences = useCallback((references: string[]) => {
    referencesRef.current = references;
    onParamChange(keys.reference, references);
    if (keys.reference !== DETAIL_REPLICA_REFERENCE_IMAGES_KEY) {
      onParamChange(DETAIL_REPLICA_REFERENCE_IMAGES_KEY, references);
    }
    onReferenceCountChange?.(references.length);
  }, [keys.reference, onParamChange, onReferenceCountChange]);

  const commitDetailReplicaScreens = useCallback((nextState: DetailReplicaState) => {
    updateReferences(nextState.references);
    onParamChange(keys.product, nextState.products);
    onParamChange(keys.model, nextState.models);
    onParamChange(keys.prompt, nextState.prompts);
    onParamChange(DETAIL_REPLICA_BLANK_FLAGS_KEY, nextState.blankFlags);
    onParamChange(DETAIL_REPLICA_RATIOS_KEY, nextState.ratios);
    onParamChange(DETAIL_REPLICA_CROP_SIZES_KEY, nextState.cropSizes);
  }, [keys.model, keys.product, keys.prompt, onParamChange, updateReferences]);

  const insertDetailReplicaScreens = useCallback((insertIndex: number, references: string[]) => {
    const safeIndex = Math.max(0, Math.min(insertIndex, state.references.length));
    const insertValues = <T,>(items: T[], values: T[]) => [
      ...items.slice(0, safeIndex),
      ...values,
      ...items.slice(safeIndex),
    ];
    const count = references.length;
    const nextState: DetailReplicaState = {
      ...state,
      references: insertValues(state.references, references),
      products: insertValues(state.products, Array.from({ length: count }, () => [])),
      models: insertValues(state.models, Array.from({ length: count }, () => [])),
      prompts: insertValues(state.prompts, Array.from({ length: count }, () => "")),
      blankFlags: insertValues(state.blankFlags, Array.from({ length: count }, () => false)),
      ratios: insertValues(state.ratios, Array.from({ length: count }, () => "")),
      cropSizes: insertValues(state.cropSizes, Array.from({ length: count }, () => "")),
    };
    commitDetailReplicaScreens(nextState);
    return nextState;
  }, [commitDetailReplicaScreens, state]);

  const deleteActiveReference = useCallback(() => {
    if (uploading) return;
    const result = removeDetailReplicaScreen(state, activeIndex);
    if (!result) return;
    deletionHistoryRef.current = [...deletionHistoryRef.current, result.deleted].slice(-50);
    // 保留 URL→宽高比缓存，便于 Ctrl/Meta+Z 撤回时按原尺寸恢复、不跳动。
    commitDetailReplicaScreens(result.state);
    onActiveIndexChange(Math.max(0, Math.min(activeIndex, result.state.references.length - 1)));
  }, [activeIndex, commitDetailReplicaScreens, onActiveIndexChange, state, uploading]);

  const undoReferenceDeletion = useCallback(() => {
    if (uploading) return;
    const deleted = deletionHistoryRef.current.at(-1);
    if (!deleted) return;
    deletionHistoryRef.current = deletionHistoryRef.current.slice(0, -1);
    const restoredState = restoreDetailReplicaScreen(state, deleted);
    // 撤回后由图片 onLoad 回填真实比例；删除前若仍有缓存则继续复用。
    commitDetailReplicaScreens(restoredState);
    onActiveIndexChange(Math.max(0, Math.min(deleted.index, restoredState.references.length - 1)));
  }, [commitDetailReplicaScreens, onActiveIndexChange, state, uploading]);

  const returnToOnboarding = useCallback(() => {
    if (uploading) return;
    deletionHistoryRef.current = [];
    setReorderOpen(false);
    aspectByUrlRef.current.clear();
    commitDetailReplicaScreens({
      ...state,
      references: [],
      products: [],
      models: [],
      prompts: [],
      blankFlags: [],
      ratios: [],
    });
    onActiveIndexChange(0);
  }, [commitDetailReplicaScreens, onActiveIndexChange, state, uploading]);

  useEffect(() => {
    deletionHistoryRef.current = [];
  }, [keys.reference]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (document.querySelector('[data-image-edit-modal="true"]')) return;
      if (event.defaultPrevented || isKeyboardEditableTarget(event.target)) return;
      const isDeleteKey = event.key === "Delete"
        || event.key === "Backspace"
        || event.code === "Delete"
        || event.code === "Backspace";
      if (isDeleteKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        if (!event.repeat) deleteActiveReference();
        return;
      }
      if (
        (event.ctrlKey || event.metaKey)
        && !event.altKey
        && !event.shiftKey
        && event.key.toLowerCase() === "z"
      ) {
        if (deletionHistoryRef.current.length === 0) return;
        event.preventDefault();
        undoReferenceDeletion();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [deleteActiveReference, undoReferenceDeletion]);

  useEffect(() => {
    if (state.references.length === 0) {
      if (activeIndex !== 0) onActiveIndexChange(0);
      return;
    }
    if (activeIndex >= state.references.length) {
      onActiveIndexChange(state.references.length - 1);
    }
  }, [activeIndex, onActiveIndexChange, state.references.length]);

  const uploadFiles = useCallback(async (files: FileList | File[], insertAfterIndex?: number) => {
    const allImageFiles = Array.from(files).filter(isImageFile);
    if (allImageFiles.length === 0 || uploading) return;

    const current = referencesRef.current;
    const insertIndex = typeof insertAfterIndex === "number"
      ? Math.max(0, Math.min(insertAfterIndex + 1, current.length))
      : current.length;
    // 反推/生图按每屏一批次提交，屏数超过上限会导致整个反推任务直接失败，这里在上传入口截断。
    const remainingSlots = Math.max(0, DETAIL_REPLICA_MAX_SCREENS - current.length);
    const imageFiles = allImageFiles.slice(0, remainingSlots);
    if (imageFiles.length === 0) {
      feedback.warning(`详情页复刻最多支持 ${DETAIL_REPLICA_MAX_SCREENS} 张参考图，请先删除部分图片`);
      return;
    }
    if (imageFiles.length < allImageFiles.length) {
      feedback.warning(`详情页复刻最多支持 ${DETAIL_REPLICA_MAX_SCREENS} 张参考图，已按顺序保留前 ${imageFiles.length} 张`);
    }

    type LocalReference = {
      file: File;
      previewUrl: string;
    };

    setUploading(true);
    onUploadingChange?.(true);
    try {
      // Object URL 创建无需解码。先以稳定顺序一次性插入本地预览，让浏览器可立即绘制，
      // 再在后台受控上传；真实宽高由已渲染的 <img> onLoad 回填。
      const prepared: LocalReference[] = imageFiles.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      prepared.forEach((item) => rememberAspectForUrl(item.previewUrl, FALLBACK_REFERENCE_ASPECT));
      let workingState = insertDetailReplicaScreens(
        insertIndex,
        prepared.map((item) => item.previewUrl),
      );

      let hasSuccessfulUpload = false;
      const completionBatcher = createFrameBatcher<{
        previewUrl: string;
        uploadedUrl?: string;
      }>((completed) => {
        let changed = false;
        completed.forEach(({ previewUrl, uploadedUrl }) => {
          const currentIndex = workingState.references.indexOf(previewUrl);
          if (currentIndex < 0) return;
          changed = true;
          if (uploadedUrl) {
            transferAspectUrl(previewUrl, uploadedUrl);
            workingState = {
              ...workingState,
              references: workingState.references.map((url) => (
                url === previewUrl ? uploadedUrl : url
              )),
            };
            hasSuccessfulUpload = true;
            URL.revokeObjectURL(previewUrl);
            return;
          }

          const removeAt = <T,>(items: T[]) => items.filter((_, index) => index !== currentIndex);
          forgetAspectForUrl(previewUrl);
          workingState = {
            ...workingState,
            references: removeAt(workingState.references),
            products: removeAt(workingState.products),
            models: removeAt(workingState.models),
            prompts: removeAt(workingState.prompts),
            blankFlags: removeAt(workingState.blankFlags),
            ratios: removeAt(workingState.ratios),
            cropSizes: removeAt(workingState.cropSizes),
          };
          URL.revokeObjectURL(previewUrl);
          feedback.error({ code: "E5003", fallback: "operation" });
        });
        if (changed) startTransition(() => commitDetailReplicaScreens(workingState));
      });

      await mapSettledWithConcurrency(prepared, async (item) => {
        try {
          const response = await ecomImageApi.uploadProductImage(item.file, { maxBytes: MAX_REVERSE_PROMPT_BYTES });
          const uploadedUrl = await resolveDetailReplicaUploadURL(response.data);
          if (!uploadedUrl) throw new Error("E5003");
          completionBatcher.enqueue({ previewUrl: item.previewUrl, uploadedUrl });
        } catch {
          completionBatcher.enqueue({ previewUrl: item.previewUrl });
        }
      }, ECOM_UPLOAD_CONCURRENCY);
      completionBatcher.flush();
      completionBatcher.dispose();

      if (hasSuccessfulUpload && typeof insertAfterIndex === "number") {
        setInsertSlotIndex((currentIndex) => (
          currentIndex === insertAfterIndex ? null : currentIndex
        ));
      }
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  }, [
    commitDetailReplicaScreens,
    forgetAspectForUrl,
    insertDetailReplicaScreens,
    onUploadingChange,
    rememberAspectForUrl,
    transferAspectUrl,
    uploading,
  ]);

  const scrollToIndex = useCallback((index: number) => {
    const safeIndex = Math.max(0, Math.min(index, state.references.length - 1));
    onActiveIndexChange(safeIndex);
    revealTokenRef.current += 1;
    setRevealTarget({ index: safeIndex, token: revealTokenRef.current });
  }, [onActiveIndexChange, state.references.length]);

  // 选中态会给参考图加 3px 描边，必须等这次渲染落地后再量高度，否则会按旧布局滚动并裁掉边缘。
  useEffect(() => {
    if (!revealTarget) return;
    const root = scrollRef.current;
    const target = imageRefs.current[revealTarget.index];
    if (!root || !target) return;
    // 立即终止仍在进行中的上一次平滑滚动：否则新定位判定「目标已完整可见」时，
    // 旧动画会继续滚到旧目标，把新选中的参考图裁在可视区外。
    root.scrollTop = root.scrollTop;
    const rootRect = root.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextScrollTop = revealReferenceScrollTop({
      scrollTop: root.scrollTop,
      viewportHeight: root.clientHeight,
      contentHeight: root.scrollHeight,
      targetTop: root.scrollTop + targetRect.top - rootRect.top,
      targetHeight: targetRect.height,
    });
    if (Math.abs(nextScrollTop - root.scrollTop) < 1) return;
    const behavior = resolveDetailReplicaRevealBehavior({
      itemCount: state.references.length,
      scrollDistance: nextScrollTop - root.scrollTop,
      viewportHeight: root.clientHeight,
    });
    programmaticScrollUntilRef.current = Date.now() + resolveDetailReplicaScrollGuardMs(behavior);
    root.scrollTo({
      top: nextScrollTop,
      behavior,
    });
  }, [revealTarget, state.references.length]);

  useEffect(() => {
    if (!focusRequest) return;
    scrollToIndex(focusRequest.index);
  }, [focusRequest, scrollToIndex]);

  // 末张展开插入框后，新增的上传框会把可滚动内容向下撑开；需同步露出该框，避免只看到末张图片。
  useEffect(() => {
    if (insertSlotIndex == null) return;
    const root = scrollRef.current;
    const placeholder = insertPlaceholderRef.current;
    if (!root || !placeholder) return;
    const rootRect = root.getBoundingClientRect();
    const placeholderRect = placeholder.getBoundingClientRect();
    const nextScrollTop = Math.min(
      root.scrollTop + Math.max(0, placeholderRect.bottom - rootRect.bottom),
      root.scrollHeight - root.clientHeight,
    );
    if (Math.abs(nextScrollTop - root.scrollTop) < 1) return;
    programmaticScrollUntilRef.current = Date.now() + resolveDetailReplicaScrollGuardMs("auto");
    root.scrollTo({ top: nextScrollTop });
  }, [insertSlotIndex]);

  const handleScroll = useCallback(() => {
    const root = scrollRef.current;
    if (!root || state.references.length === 0) return;
    if (Date.now() < programmaticScrollUntilRef.current) return;
    const center = root.getBoundingClientRect().top + root.clientHeight / 2;
    let nextIndex = activeIndex;
    let nextDistance = Number.POSITIVE_INFINITY;
    imageRefs.current.forEach((element, index) => {
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - center);
      if (distance < nextDistance) {
        nextDistance = distance;
        nextIndex = index;
      }
    });
    if (nextIndex !== activeIndex) onActiveIndexChange(nextIndex);
  }, [activeIndex, onActiveIndexChange, state.references.length]);

  const releaseProgrammaticScrollGuard = useCallback(() => {
    programmaticScrollUntilRef.current = 0;
  }, []);

  const commitReorderedScreens = useCallback((selectedIndexes: number[]) => {
    const reorder = <T,>(items: T[]) => selectedIndexes.map((index) => items[index]);
    commitDetailReplicaScreens({
      ...state,
      references: reorder(state.references),
      products: reorder(state.products),
      models: reorder(state.models),
      prompts: reorder(state.prompts),
      blankFlags: reorder(state.blankFlags),
      ratios: reorder(state.ratios),
      cropSizes: reorder(state.cropSizes),
    });
    const nextActiveIndex = selectedIndexes.indexOf(activeIndex);
    onActiveIndexChange(nextActiveIndex >= 0 ? nextActiveIndex : 0);
    setReorderOpen(false);
  }, [activeIndex, commitDetailReplicaScreens, onActiveIndexChange, state]);

  const handleThumbnailDragStart = useCallback((event: DragStartEvent) => {
    setDraggedThumbnailIndex(state.references.indexOf(String(event.active.id)));
  }, [state.references]);

  const handleThumbnailDragCancel = useCallback(() => {
    setDraggedThumbnailIndex(null);
  }, []);

  const handleThumbnailDragEnd = useCallback((event: DragEndEvent) => {
    setDraggedThumbnailIndex(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = state.references.indexOf(String(active.id));
    const toIndex = state.references.indexOf(String(over.id));
    if (fromIndex < 0 || toIndex < 0) return;
    const nextOrder = moveDetailReplicaIndexOrder(
      state.references.length,
      fromIndex,
      toIndex,
    );
    commitReorderedScreens(nextOrder);
  }, [commitReorderedScreens, state.references]);

  if (!gate.hasReferences) {
    return typeof document !== "undefined" ? createPortal(
      <div
        data-detail-replica-onboarding
        className="fixed bottom-0 right-0 top-[64px] z-10 left-[72px] flex min-h-0 flex-col items-center justify-center overflow-hidden bg-[#000] max-md:top-[56px] max-md:left-0"
      >
        <EcomOnboardingGlowLights navWidthPx={72} />
        {tutorialConfig && (
          <button
            ref={tutorialButtonRef}
            type="button"
            data-detail-replica-tutorial-button
            aria-haspopup="dialog"
            aria-expanded={tutorialPreviewOpen}
            aria-controls="detail-replica-tutorial-preview"
            onMouseEnter={() => openTutorialPreview(false)}
            onMouseLeave={scheduleTutorialPreviewClose}
            onFocus={() => openTutorialPreview(false)}
            onClick={() => {
              if (tutorialPreviewOpen && tutorialPreviewPinned) closeTutorialPreview();
              else openTutorialPreview(true);
            }}
            onBlur={(event) => {
              const nextTarget = event.relatedTarget as Node | null;
              if (!nextTarget || !tutorialPreviewRef.current?.contains(nextTarget)) {
                scheduleTutorialPreviewClose();
              }
            }}
            className="absolute right-14 top-14 z-20 mt-5 inline-flex h-9 items-center gap-1.5 rounded-[8px] border border-[#272727] bg-[#272727] px-4 text-[13px] text-[#8C8C8C] transition-colors hover:border-[#272727] hover:bg-[#272727] hover:text-[#F6F6F6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current max-md:right-4"
          >
            <CircleHelp aria-hidden="true" className="size-3.5" />
            {tutorialConfig.button_label}
          </button>
        )}
        <h1 className="relative z-10 mb-8 bg-linear-to-r from-[#ABADB0] via-white to-[#ABADB0] bg-clip-text text-center font-['Noto_Sans_SC','PingFang_SC','Microsoft_YaHei',sans-serif] text-[48px] leading-[1.5] font-medium tracking-tight text-transparent [text-shadow:0_4px_32px_rgba(255,255,255,0.25)]">
          一键复刻爆款详情页
        </h1>
        <div
          data-detail-replica-upload-shell
          className={`relative z-10 flex h-[212px] w-[458px] items-center justify-center rounded-[20px] border bg-[#131313] transition-colors ${
            dragOver ? "border-[#00FAE9]" : "border-[#2D2D2D]"
          }`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            void uploadFiles(event.dataTransfer.files);
          }}
        >
          <label
            className={`flex h-[184px] w-[426px] cursor-pointer flex-col items-center justify-center rounded-[20px] border bg-[#131313] transition-colors ${
              dragOver ? "border-[#00FAE9]" : "border-[#2D2D2D] hover:border-[#686868]"
            }`}
          >
            <input
              className="sr-only"
              type="file"
              accept={IMAGE_ACCEPT}
              multiple
              disabled={uploading}
              onChange={(event) => {
                if (event.target.files) void uploadFiles(event.target.files);
                event.currentTarget.value = "";
              }}
            />
            {uploading ? (
              <Loader2 className="mb-3 h-6 w-6 animate-spin text-white/70" />
            ) : (
              <Plus className="mb-3 h-6 w-6 text-[#D9D9D9]" />
            )}
            <span className="text-[14px] font-medium text-[#F6F6F6]">
              {uploading ? "图片上传中..." : "点击上传图片或拖拽图片到此处"}
            </span>
            <span className="mt-2 text-[12px] text-white/45">支持 JPG、PNG 格式，支持多张上传</span>
          </label>
        </div>
        <ol className="relative z-10 mt-7 flex items-center gap-4 text-[13px] text-[#F6F6F6]">
          {["上传参考图", "根据每屏参考图内容上传对应产品图", "设置字体生成提示词", "开始生图"].map((label, index) => (
            <li key={label} className="flex items-center gap-3">
              <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#FAFBFF] text-[14px] text-[#181818]">
                {index + 1}
              </span>
              <span>{label}</span>
              {index < 3 && <span className="tracking-[2px] text-white/45">……</span>}
            </li>
          ))}
        </ol>
        {tutorialPreviewPosition && tutorialConfig && (
          <EcomTutorialPopover
            open={tutorialPreviewOpen}
            id="detail-replica-tutorial-preview"
            ariaLabel={tutorialConfig.title || tutorialConfig.button_label}
            position={tutorialPreviewPosition}
            popoverRef={tutorialPreviewRef}
            onMouseEnter={clearTutorialCloseTimer}
            onMouseLeave={scheduleTutorialPreviewClose}
            onBlur={(event) => {
              const nextTarget = event.relatedTarget as Node | null;
              if (!nextTarget || (!tutorialPreviewRef.current?.contains(nextTarget) && !tutorialButtonRef.current?.contains(nextTarget))) {
                scheduleTutorialPreviewClose();
              }
            }}
          >
            <div data-detail-replica-tutorial-preview className="max-h-[min(70vh,560px)] overflow-y-auto p-4">
              {(tutorialConfig.image_url || tutorialConfig.media_url) && (
                <div className="mb-4 overflow-hidden rounded-[12px] bg-black/35">
                  {tutorialConfig.image_url || tutorialConfig.media_type === "image" ? (
                    <img
                      src={tutorialConfig.image_url || tutorialConfig.media_url}
                      alt={tutorialConfig.title}
                      className="block h-[174px] w-full object-cover"
                    />
                  ) : (
                    <video
                      src={tutorialConfig.media_url}
                      muted
                      playsInline
                      autoPlay
                      loop
                      className="block h-[174px] w-full object-cover"
                    />
                  )}
                </div>
              )}
              {tutorialConfig.title && <div className="mb-2 text-[18px] font-bold leading-6 text-white">{tutorialConfig.title}</div>}
              {tutorialConfig.content && <div className="whitespace-pre-wrap text-[14px] leading-6 text-white/80">{tutorialConfig.content}</div>}
              {(tutorialConfig.detail_url || !tutorialDismissed) && (
                <div className="mt-4 flex justify-end gap-3">
                  {!tutorialDismissed && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="medium"
                      data-detail-replica-tutorial-dismiss
                      onClick={dismissTutorial}
                    >
                      不再提示
                    </Button>
                  )}
                  {tutorialConfig.detail_url && (
                    <Button
                      type="button"
                      variant="default"
                      size="medium"
                      data-detail-replica-tutorial-detail
                      onClick={() => {
                        clearTutorialCloseTimer();
                        setTutorialPreviewOpen(false);
                        setTutorialPreviewPinned(false);
                        setTutorialOpen(true);
                      }}
                    >
                      查看详情
                    </Button>
                  )}
                </div>
              )}
            </div>
          </EcomTutorialPopover>
        )}
        {tutorialConfig && (
          tutorialConfig.detail_media_type === "video" && tutorialConfig.detail_url ? (
            <EcomTutorialVideoModal
              open={tutorialOpen}
              onClose={() => setTutorialOpen(false)}
              title={tutorialConfig.title}
              videoUrl={tutorialConfig.detail_url}
              returnFocusRef={tutorialButtonRef}
            />
          ) : (
            <EcomModal
              open={tutorialOpen}
              onClose={() => setTutorialOpen(false)}
              title={tutorialConfig.title}
              layer="tutorial"
              closeLabel="关闭教程详情图片"
              width="min(1000px, calc(100vw - 40px))"
              afterClose={() => {
                if (!tutorialOpenRef.current) tutorialButtonRef.current?.focus();
              }}
              classNames={{
                mask: "bg-black/20 backdrop-blur-[7.5px]",
                panel: "max-h-[calc(100vh-64px)] overflow-hidden rounded-[16px] bg-black shadow-[0_24px_80px_rgba(0,0,0,0.7)]",
                header: "border-b border-white/10 bg-black px-[15px]",
                body: "p-0",
              }}
            >
              <div
                data-detail-replica-tutorial-modal
                data-detail-replica-tutorial-panel
                data-detail-replica-tutorial-content
                className="flex aspect-video max-h-[calc(100vh-150px)] w-full min-h-0 items-center justify-center bg-black"
              >
                {tutorialConfig.detail_url || tutorialConfig.image_url ? (
                  <img
                    data-detail-replica-tutorial-image
                    src={tutorialConfig.detail_url || tutorialConfig.image_url}
                    alt={tutorialConfig.title}
                    className="h-full w-full object-contain"
                    decoding="async"
                  />
                ) : (
                  <p className="py-8 text-center text-sm text-white/45">暂无教程媒体</p>
                )}
              </div>
            </EcomModal>
          )
        )}
      </div>,
      document.body,
    ) : null;
  }

  return (
    <div data-detail-replica-workspace className="relative flex h-full min-h-0 flex-col bg-[#000] text-white">
      <div data-detail-replica-workspace-toolbar className="flex h-14 shrink-0 items-center justify-between px-5">
        <EcomBackIcon
          data-detail-replica-back
          aria-label="返回上传首页"
          disabled={uploading}
          onClick={returnToOnboarding}
        />
        <Button
          type="button"
          variant="outline"
          size="small"
          data-detail-replica-reorder
          onClick={() => startTransition(() => setReorderOpen(true))}
        >
          重新排序
        </Button>
      </div>

      {uploadHoverPreview?.url && (
        <div
          data-detail-replica-upload-hover-preview
          className="pointer-events-none absolute right-3 top-[60px] z-30 inline-flex overflow-hidden"
        >
          <SafeImage
            src={uploadHoverPreview.url}
            alt={uploadHoverPreview.alt || "上传图片预览"}
            className="block h-auto w-auto max-h-[min(360px,50vh)] max-w-[min(320px,42vw)] object-contain"
            decoding="async"
          />
        </div>
      )}

      <div data-detail-replica-reference-layout className="flex min-h-0 flex-1 items-start justify-center gap-5 overflow-hidden px-5 pb-5">
        <div
          ref={scrollRef}
          data-detail-replica-reference-scroll
          onScroll={handleScroll}
          onWheel={releaseProgrammaticScrollGuard}
          onPointerDown={releaseProgrammaticScrollGuard}
          className="h-full min-w-0 overflow-y-auto pl-[116px] max-[700px]:pl-0 [scrollbar-color:rgba(255,255,255,.18)_transparent] [scrollbar-width:thin]"
        >
          <div className="mx-auto flex w-[400px] max-w-full flex-col">
            {state.references.map((url, index) => {
              const selected = activeIndex === index;
              const aspectRatio = aspectByUrlRef.current.get(url) || FALLBACK_REFERENCE_ASPECT;
              return (
                <div key={`detail-replica-ref-${index}`} className="w-full">
                  <div className="relative w-full">
                    <button
                      ref={(element) => { imageRefs.current[index] = element; }}
                      type="button"
                      data-detail-replica-selected-reference={selected ? index + 1 : undefined}
                      data-detail-replica-reference-aspect={aspectRatio.toFixed(4)}
                      onClick={() => scrollToIndex(index)}
                      className={`group relative block w-full cursor-pointer overflow-hidden bg-transparent p-0 text-left border-[3px] border-solid ${
                        selected ? "" : "border-transparent"
                      }`}
                      style={{
                        aspectRatio: String(aspectRatio),
                        ...(selected ? {
                          borderColor: SELECTED_REFERENCE_BORDER,
                        } : undefined),
                      }}
                      aria-current={selected ? "true" : undefined}
                      aria-label={`查看第 ${index + 1} 屏参考图`}
                    >
                      <img
                        src={url}
                        alt={`第 ${index + 1} 屏参考图`}
                        className="block h-full w-full bg-[#111] object-contain"
                        onLoad={(event) => {
                          const image = event.currentTarget;
                          if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                            rememberAspectForUrl(url, image.naturalWidth / image.naturalHeight, true);
                          }
                          // 等图片完整解码后再移除蒙版，避免大图逐行加载露出。
                          void image.decode().catch(() => undefined).then(() => markReferenceLoaded(url));
                        }}
                        onError={() => markReferenceLoaded(url)}
                      />
                      {isReferencePending(url) && (
                        <span
                          data-detail-replica-reference-loading-mask
                          className="absolute inset-0 z-10 flex items-center justify-center bg-black/45"
                        >
                          <span className="size-6 rounded-full border-2 border-white/30 border-t-white/80 animate-spin" />
                        </span>
                      )}
                      <span className="absolute bottom-2 left-2 z-20 flex h-6 min-w-6 items-center justify-center rounded bg-black/70 px-1.5 text-[12px] text-white">
                        {index + 1}
                      </span>
                    </button>
                    {selected && (
                      <button
                        type="button"
                        data-detail-replica-insert-trigger
                        aria-expanded={insertSlotIndex === index}
                        disabled={uploading}
                        onClick={() => setInsertSlotIndex((currentIndex) => (
                          currentIndex === index ? null : index
                        ))}
                        className="absolute bottom-0 right-[calc(100%+12px)] z-10 flex h-8 cursor-pointer items-center gap-1 whitespace-nowrap rounded-[8px] border border-[#27282A] bg-black px-[15px] text-[13px] text-[#E6E6E6] hover:bg-[#111] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        插入图片
                        <span className={`block size-3 transition-transform ${
                          insertSlotIndex === index ? "rotate-180" : ""
                        }`}>
                          <img
                            src="/ecom/detail-replica-insert-chevron.svg"
                            alt=""
                            className="size-full"
                          />
                        </span>
                      </button>
                    )}
                  </div>
                  {insertSlotIndex === index && (
                    <label
                      ref={insertPlaceholderRef}
                      data-detail-replica-insert-placeholder
                      data-detail-replica-insert-drop-active={insertDragOverIndex === index ? "true" : undefined}
                      aria-label={uploading ? "图片上传中" : "添加图片"}
                      onDragOver={(event) => {
                        event.preventDefault();
                        if (!uploading) setInsertDragOverIndex(index);
                      }}
                      onDragLeave={(event) => {
                        if (event.currentTarget.contains(event.relatedTarget as Node)) return;
                        setInsertDragOverIndex((currentIndex) => currentIndex === index ? null : currentIndex);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        setInsertDragOverIndex((currentIndex) => currentIndex === index ? null : currentIndex);
                        void uploadFiles(event.dataTransfer.files, index);
                      }}
                      className={`mt-2 mb-2 flex h-[132px] w-full max-w-full cursor-pointer items-center justify-center rounded-[12px] border border-dashed bg-[#111] transition-colors ${
                        insertDragOverIndex === index
                          ? "border-[#00FAE9] bg-[#102322] shadow-[0_0_0_1px_rgba(0,250,233,0.2)]"
                          : "border-[#555] hover:border-[#888] hover:bg-[#151515]"
                      } ${uploading ? "cursor-not-allowed opacity-70" : ""}`}
                    >
                      <input
                        className="sr-only"
                        type="file"
                        accept={IMAGE_ACCEPT}
                        multiple
                        disabled={uploading}
                        onChange={(event) => {
                          if (event.target.files) void uploadFiles(event.target.files, index);
                          event.currentTarget.value = "";
                        }}
                      />
                      {uploading ? (
                        <Loader2 className="size-5 animate-spin text-[#E6E6E6]" />
                      ) : (
                        <Plus className="size-5 text-[#E6E6E6]" />
                      )}
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div
          ref={thumbnailRailRef}
          data-detail-replica-thumbnail-rail
          className="flex h-full w-[52px] shrink-0 items-end justify-center overflow-hidden"
        >
          <DndContext
            sensors={thumbnailSensors}
            collisionDetection={closestCenter}
            onDragStart={handleThumbnailDragStart}
            onDragCancel={handleThumbnailDragCancel}
            onDragEnd={handleThumbnailDragEnd}
          >
            <SortableContext
              items={state.references.map((url) => url)}
              strategy={verticalListSortingStrategy}
            >
              <div
                data-detail-replica-thumbnail-stack
                data-detail-replica-thumbnail-scale={thumbnailSize.scale.toFixed(4)}
                className="flex max-h-full flex-col overflow-hidden rounded-[8px]"
                style={{ width: thumbnailSize.width }}
              >
                {state.references.map((url, index) => (
                  <SortableDetailReplicaThumbnail
                    key={url}
                    url={url}
                    sortableId={url}
                    index={index}
                    width={thumbnailSize.width}
                    height={thumbnailSize.height}
                    selected={activeIndex === index}
                    onSelect={scrollToIndex}
                  />
                ))}
              </div>
            </SortableContext>
            {typeof document !== "undefined" && createPortal(
              <DragOverlay dropAnimation={null} zIndex={1000}>
                {draggedThumbnailIndex != null && state.references[draggedThumbnailIndex] ? (
                  <div
                    data-detail-replica-thumbnail-drag-overlay
                    className="pointer-events-none overflow-hidden rounded-[8px] bg-[#111] opacity-95 ring-2 ring-white/85 shadow-[0_14px_36px_rgba(0,0,0,0.68)]"
                    style={{ width: 84, height: 118 }}
                  >
                    <img
                      src={state.references[draggedThumbnailIndex]}
                      alt=""
                      draggable={false}
                      className="block size-full object-contain"
                    />
                  </div>
                ) : null}
              </DragOverlay>,
              document.body,
            )}
          </DndContext>
        </div>
      </div>
      <DetailPageReplicaReorderDialog
        images={state.references}
        aspectRatios={state.references.map(
          (url) => aspectByUrlRef.current.get(url) || FALLBACK_REFERENCE_ASPECT,
        )}
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        onSave={commitReorderedScreens}
      />
    </div>
  );
}
